import { CreateSessionResponse, CreateUserResponse } from '@insforge/shared-schemas';
import { useInsforge } from '../provider/InsforgeProvider';

/**
 * Hook to access authentication methods
 *
 * @returns Object containing:
 *   - `signIn`: Function to sign in with email and password
 *   - `signUp`: Function to sign up with email and password
 *   - `signOut`: Function to sign out the current user
 *   - `isLoaded`: Boolean indicating if auth state has been loaded
 *   - `isSignedIn`: Boolean indicating if user is currently signed in
 *
 * @example
 * ```tsx
 * function LoginForm() {
 *   const { signIn, signUp, signOut, isLoaded, isSignedIn } = useAuth();
 *
 *   async function handleLogin(email: string, password: string) {
 *     try {
 *       await signIn(email, password);
 *       // User is now signed in
 *     } catch (error) {
 *       console.error('Sign in failed:', error);
 *     }
 *   }
 *
 *   if (!isLoaded) return <div>Loading...</div>;
 *
 *   return (
 *     <form onSubmit={(e) => { e.preventDefault(); handleLogin(email, password); }}>
 *       {/* form fields *\/}
 *     </form>
 *   );
 * }
 * ```
 */
export function useAuth(): {
  signIn: (
    email: string,
    password: string
  ) => Promise<CreateSessionResponse | { error: string; statusCode?: number; errorCode?: string }>;
  signUp: (
    email: string,
    password: string
  ) => Promise<CreateUserResponse | { error: string; statusCode?: number; errorCode?: string }>;
  signOut: () => Promise<void>;
  isLoaded: boolean;
  isSignedIn: boolean | undefined;
} {
  const { signIn, signUp, signOut, isLoaded, isSignedIn } = useInsforge();
  return { signIn, signUp, signOut, isLoaded, isSignedIn };
}
